import { useCallback, useMemo, useState, type ReactNode } from 'react';

import type { EntityTableFilterParams } from '@/components/map/utils/entityMapFilters';
import { StatsFiltersContext, statsFiltersKey } from './StatsFiltersContext';

/**
 * Bridges the committed map filters from MapView (inside the route <Outlet>) up to
 * the Global Stats bar's fetch hooks in the Find & Fix layout — the stats bar must
 * respect the active filters, and the filter state machine lives with the map's
 * Filters panel. Same bridge shape as StatsScopeProvider; setFilters bails out when
 * the serialised form is unchanged so republishing on unrelated renders is free.
 */
export function StatsFiltersProvider({ children }: { children: ReactNode }) {
    const [filters, setFilters] = useState<EntityTableFilterParams | null>(null);

    const publishFilters = useCallback((next: EntityTableFilterParams | null) => {
        setFilters((current) => (statsFiltersKey(current) === statsFiltersKey(next) ? current : next));
    }, []);

    const value = useMemo(
        () => ({ filters, filtersKey: statsFiltersKey(filters), setFilters: publishFilters }),
        [filters, publishFilters],
    );

    return <StatsFiltersContext value={value}>{children}</StatsFiltersContext>;
}
